import { useState } from "react";
import { Link } from "react-router-dom";
import { Ban, Loader2 } from "lucide-react";
import { WaitingNotice, isInContinuationChain } from "@/components/mailbox/job-chain";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useActiveJob, useCancelJob } from "@/hooks/queries/use-mailbox";
import { useToast } from "@/hooks/useToast";
import { ApiError } from "@/lib/api/client";
import type { ActiveJobQuery } from "@/lib/api/mailbox";
import { useRunProgress } from "@/hooks/useRunProgress";
import { runCountLabel, runStateLabel } from "@/lib/run-progress-format";

interface ActiveJobCancelProps {
  /** The epic (its build) or epic request (its plan) to look up a running job for. */
  query: ActiveJobQuery;
  /** What the running job is called here, e.g. "A build" or "Planning". */
  label: string;
}

/**
 * "A build is running · View · Cancel" for the epic and request pages
 * (ENG-E56 follow-up). Renders nothing while no plan/build is running for the
 * item. The cancel route still checks that the person owns the job or is an
 * org admin. A build parked on the AI usage limit shows when it resumes, and a
 * continuation offers to cancel the whole chain (ENG-335).
 */
export function ActiveJobCancel({ query, label }: ActiveJobCancelProps) {
  const { data: job } = useActiveJob(query);
  // Progress text comes from the run's RunProgress, the same source as the
  // epic and job pages (ENG-370). Only fetched while a job is running.
  const runKind = "epicId" in query ? "build" : "planning";
  const runId = "epicId" in query ? query.epicId : query.epicRequestId;
  const { progress } = useRunProgress(runKind, job ? runId : null);
  const cancelJob = useCancelJob();
  const { toast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!job) return null;
  const cancelling = job.status === "cancelling";
  const waiting = job.status === "waiting";
  // A build parked on a question for its owner (ENG-340).
  const needsInput = waiting && job.waitingReason === "needs_input";
  const replyReceived = waiting && job.waitingReason === "reply_received";
  const firstLine = job.needsInput?.question.split("\n").find((l) => l.trim())?.trim() ?? "";
  const inChain = isInContinuationChain(job);

  const handleConfirm = () => {
    cancelJob.mutate(job.id, {
      onError: (error) => {
        toast(error instanceof ApiError ? error.message : "Failed to cancel the job");
      },
    });
    setConfirmOpen(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="active-job-cancel">
      {needsInput ? (
        <span className="flex flex-wrap items-center gap-1.5" data-testid="active-job-needs-input">
          <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
            Waiting for your reply
          </span>
          {firstLine && <span className="break-words">{firstLine}</span>}
          <Link to={`/mailbox/jobs/${job.id}`} className="text-primary hover:underline">
            Answer
          </Link>
        </span>
      ) : replyReceived ? (
        <span>Answer received — resuming.</span>
      ) : waiting ? (
        <WaitingNotice resumeAt={job.resumeAt} />
      ) : (
        <>
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          <span>{cancelling ? `${label} is being cancelled…` : `${label} is running.`}</span>
        </>
      )}
      {progress && progress.total > 0 && (
        <span className="text-xs text-muted-foreground" data-testid="active-job-progress">
          {runCountLabel(progress)}
          {runStateLabel(progress.state) && <> · {runStateLabel(progress.state)}</>}
        </span>
      )}
      <Link to={`/mailbox/jobs/${job.id}`} className="text-primary hover:underline">
        View
      </Link>
      {!cancelling && (
        <Button
          variant="outline"
          size="sm"
          disabled={cancelJob.isPending}
          onClick={() => {
            setConfirmOpen(true);
          }}
        >
          <Ban className="h-4 w-4 mr-1.5" />
          {inChain ? "Cancel build chain" : "Cancel"}
        </Button>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{inChain ? "Cancel the whole build chain?" : "Cancel this run?"}</DialogTitle>
            <DialogDescription>
              {inChain ? "This run and every continuation after it stop." : "The run stops."} Nothing is pushed and no pull request is opened; any work so far is kept on its branch.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmOpen(false);
              }}
            >
              Keep running
            </Button>
            <Button variant="destructive" disabled={cancelJob.isPending} onClick={handleConfirm}>
              {inChain ? "Cancel build chain" : "Cancel run"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
